import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GrowthCreditEntry } from '@feathr/blackbox';
import { EGrowthCreditEntryClass } from '@feathr/blackbox';
import { Button, CardV2 as Card, DatePicker, Table } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, TimeFormat } from '@feathr/hooks';
import type { IObject } from '@feathr/rachis';

import { creditsRedeemedColumns } from './creditsRedeemedColumns';
import ExportRedemptionsModal from './ExportRedemptionsModal';

import * as styles from './CreditsRedeemedTable.css';

const REDEMPTION_CLASSES = [
  EGrowthCreditEntryClass.MediaRedemption,
  EGrowthCreditEntryClass.InvoiceRedemption,
];

function CreditsRedeemedTable(): JSX.Element {
  const { t } = useTranslation();
  const { GrowthCreditEntries } = useStore();
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  const handleOpenExportModal = useCallback((): void => {
    setIsExportModalOpen(true);
  }, []);

  const handleCloseExportModal = useCallback((): void => {
    setIsExportModalOpen(false);
  }, []);

  const filters: IObject = useMemo(() => {
    const result: IObject = {
      _cls__in: REDEMPTION_CLASSES,
    };

    if (startDate) {
      result.date_entered__gte = startDate;
    }

    if (endDate) {
      result.date_entered__lte = `${endDate}T23:59:59.999Z`;
    }

    return result;
  }, [startDate, endDate]);

  function handleStartDateChange(date?: string): void {
    setStartDate(date);
  }

  function handleStartDateClear(): void {
    setStartDate(undefined);
  }

  function handleEndDateChange(date?: string): void {
    setEndDate(date);
  }

  function handleEndDateClear(): void {
    setEndDate(undefined);
  }

  const columns = useMemo(() => creditsRedeemedColumns({ t }), [t]);

  const filterBar = (
    <div className={styles.filterBar}>
      <div className={styles.filters}>
        <DatePicker
          format={TimeFormat.isoDate}
          isClearable={true}
          onClear={handleStartDateClear}
          onDateStrChange={handleStartDateChange}
          placeholder={t('Start date')}
          showCalendarIcon={true}
          value={startDate}
        />
        <DatePicker
          format={TimeFormat.isoDate}
          isClearable={true}
          onClear={handleEndDateClear}
          onDateStrChange={handleEndDateChange}
          placeholder={t('End date')}
          showCalendarIcon={true}
          value={endDate}
        />
      </div>
    </div>
  );

  return (
    <>
      <Card width={'full'}>
        <Card.Header
          description={t('Track your credit redemptions and transactions')}
          title={t('Redemption History')}
        >
          <Button
            onClick={handleOpenExportModal}
            prefix={getIconForAction('export')}
            type={'secondary'}
          >
            {t('Export as CSV')}
          </Button>
        </Card.Header>
        <Card.Content>
          {filterBar}
          <Table<GrowthCreditEntry>
            collection={GrowthCreditEntries}
            columns={columns}
            filters={filters}
            initialPagesize={10}
            initialSort={[{ id: 'date_entered', desc: true }]}
          />
        </Card.Content>
      </Card>
      <ExportRedemptionsModal onClose={handleCloseExportModal} opened={isExportModalOpen} />
    </>
  );
}

export default observer(CreditsRedeemedTable);
